import { getHttpClient } from './httpClient';

export type PerfRunStatus = 'pending' | 'running' | 'passed' | 'failed' | 'cancelled' | 'error';

export interface ScenarioDef {
  name: string;
  scriptPath: string;
  description: string;
  defaultProfile: string;
  allowedProfiles: string[];
}

export interface PerfTargets {
  p95CrudMs: number;
  p95AggregationMs: number;
  p95LlmMs: number;
  p95SseMs: number;
  p95AuthMs: number;
  errorRate: number;
}

export interface ScenariosResponse {
  scenarios: ScenarioDef[];
  profileDurationsSec: Record<string, number>;
  allowedTargets: string[];
  caps: { maxVus: number; maxDurationSeconds: number };
  targets: PerfTargets;
}

export interface PerfRun {
  id: string;
  scenario_name: string;
  profile: string;
  target_url: string;
  status: PerfRunStatus;
  started_by: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  p95_crud_ms: string | number | null;
  p95_aggregation_ms: string | number | null;
  p95_llm_ms: string | number | null;
  p95_auth_ms: string | number | null;
  p95_sse_ms: string | number | null;
  http_req_failed_rate: string | number | null;
  total_requests: number | null;
  total_iterations: number | null;
  thresholds_json: Record<string, unknown> | null;
  summary_json: Record<string, unknown> | null;
  status_codes_summary: Record<string, Record<string, number>> | null;
  report_html_path: string | null;
  cancellation_reason: string | null;
  error_message: string | null;
}

export interface MetricsWindow {
  runId: string;
  windowStartMs: number;
  windowEndMs: number;
  rps: number;
  errorRate: number;
  vus: number;
  p95: {
    crud: number | null;
    aggregation: number | null;
    llm: number | null;
    sse: number | null;
    auth: number | null;
    overall: number | null;
  };
}

export async function getScenarios(): Promise<ScenariosResponse> {
  return (await getHttpClient().get<ScenariosResponse>('/api/admin/perf-tests/scenarios')).data;
}

export interface ServiceStatus {
  running: boolean;
  containerId?: string;
  detail?: string;
}

export interface StackStatus {
  postgres: ServiceStatus;
  redis: ServiceStatus;
  mockLlm: ServiceStatus;
  targetBackend: ServiceStatus & { url: string };
  ready: boolean;
}

export async function getStackStatus(): Promise<StackStatus> {
  return (await getHttpClient().get<StackStatus>('/api/admin/perf-tests/stack/status')).data;
}

export async function listRuns(opts?: { limit?: number; status?: string; scenario?: string }): Promise<{ runs: PerfRun[] }> {
  return (await getHttpClient().get('/api/admin/perf-tests/runs', { params: opts })).data;
}

export interface RunInterpretation {
  verdict: 'PASS' | 'WARN' | 'FAIL';
  headline: string;
  insights: string[];
  suggestedActions: string[];
}

export async function getRun(id: string): Promise<{ run: PerfRun; interpretation: RunInterpretation }> {
  return (await getHttpClient().get(`/api/admin/perf-tests/runs/${id}`)).data;
}

export async function createRun(payload: {
  scenarioName: string;
  profile: string;
  targetUrl: string;
}): Promise<{ run: PerfRun }> {
  return (await getHttpClient().post('/api/admin/perf-tests/runs', payload)).data;
}

export async function cancelRun(id: string): Promise<{ ok: boolean }> {
  return (await getHttpClient().post(`/api/admin/perf-tests/runs/${id}/cancel`)).data;
}

export async function deleteRun(id: string): Promise<{ ok: boolean; deleted: number }> {
  return (await getHttpClient().delete(`/api/admin/perf-tests/runs/${id}`)).data;
}

export async function purgeRuns(): Promise<{ ok: boolean; deleted: number }> {
  return (await getHttpClient().delete('/api/admin/perf-tests/runs')).data;
}

// EventSource et iframe ne peuvent pas porter de header Authorization,
// alors on inclut le JWT en query param. Le backend accepte ce mode auth
// uniquement pour /stream et /report (cf perfTestsRoutes.ts).
function getAdminToken(): string {
  return sessionStorage.getItem('admin_token') ?? '';
}

export function getReportUrl(id: string): string {
  const token = encodeURIComponent(getAdminToken());
  return `/api/admin/perf-tests/runs/${id}/report?token=${token}`;
}

export function getStreamUrl(id: string): string {
  const token = encodeURIComponent(getAdminToken());
  return `/api/admin/perf-tests/runs/${id}/stream?token=${token}`;
}

export function openMetricsStream(
  id: string,
  handlers: {
    onOpen?: () => void;
    onWindow?: (w: MetricsWindow) => void;
    onEnd?: (status: string) => void;
    onError?: (err: Event) => void;
  },
): EventSource {
  const es = new EventSource(getStreamUrl(id), { withCredentials: true });
  es.addEventListener('open', () => handlers.onOpen?.());
  es.addEventListener('window', (ev) => {
    try {
      const data = JSON.parse((ev as MessageEvent).data) as MetricsWindow;
      handlers.onWindow?.(data);
    } catch {
      /* ignore */
    }
  });
  es.addEventListener('end', (ev) => {
    try {
      const data = JSON.parse((ev as MessageEvent).data) as { status: string };
      handlers.onEnd?.(data.status);
    } catch {
      handlers.onEnd?.('unknown');
    }
    es.close();
  });
  es.onerror = (err) => handlers.onError?.(err);
  return es;
}
